import React, { useCallback } from "react";
import {
  AlertTriangle,
  BookOpen,
  Building2,
  ClipboardCheck,
  ClipboardPlus,
  FilePlus2,
  UserPlus,
  Users,
} from "lucide-react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { ErrorState } from "../components/FeedbackStates";
import { DashboardNeedsAttention } from "../components/DashboardNeedsAttention";
import { ClinicianAssistantHeaderButton } from "../components/ClinicianAssistantTrigger";
import { ClinicianPortalLayout } from "../components/PortalLayout";
import { PortalLink } from "../components/PortalLink";
import {
  addActivityTemplatePath,
  addClientPath,
  addHomeworkActivityPath,
} from "../config/navigation";
import { usePortalData } from "../hooks/usePortalData";

export function PortalHomePage({ clinicianCode }: { clinicianCode: string }) {
  return (
    <PortalHomePageForClinician
      key={clinicianCode}
      clinicianCode={clinicianCode}
    />
  );
}

function PortalHomePageForClinician({
  clinicianCode,
}: {
  clinicianCode: string;
}) {
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getMyClients(clinicianCode, signal),
    [clinicianCode],
  );
  const state = usePortalData(request);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(data?.found && data.therapist);
  const advancedDashboardEnabled = available
    ? (data?.therapist?.advancedDashboardEnabled ?? true)
    : undefined;
  const attentionRequest = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getDashboardAttention(clinicianCode, signal),
    [clinicianCode],
  );
  const attentionState = usePortalData(attentionRequest, {
    enabled: advancedDashboardEnabled === true,
  });
  const isSupervisor = available && data?.therapist?.role === "supervisor";
  const teamRequest = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getSupervisorTeamOverview(clinicianCode, "", signal),
    [clinicianCode],
  );
  const teamState = usePortalData(teamRequest, { enabled: isSupervisor });
  const clinicianName = available ? data?.therapist?.therapistName : "";
  const clinicNames = available
    ? Array.from(
        new Set(
          (data?.therapist?.clinicNames ?? [])
            .map((clinicName) => clinicName.trim())
            .filter(Boolean),
        ),
      )
    : [];
  const clientCount = available ? data?.patients.length : undefined;
  const attentionData =
    attentionState.status === "success" ||
    attentionState.status === "refreshing"
      ? attentionState.data
      : undefined;
  const attentionAvailable = Boolean(
    attentionData?.found && attentionData.authorized && attentionData.therapist,
  );
  const attentionLoading =
    attentionState.status === "loading" ||
    (attentionState.status === "refreshing" && !attentionAvailable);
  const teamData =
    teamState.status === "success" || teamState.status === "refreshing"
      ? teamState.data
      : undefined;
  const teamAvailable = Boolean(
    teamData?.found &&
      teamData.authorized &&
      teamData.therapist?.role === "supervisor",
  );
  const teamUnavailable =
    teamState.status === "error" || Boolean(teamData && !teamAvailable);

  return (
    <ClinicianPortalLayout
      activeGlobalKey="portal"
      clinicianCode={clinicianCode}
      therapistRole={available ? data?.therapist?.role : undefined}
    >
      <div className="cp-page-content cp-portal-home">
        <section aria-labelledby="portal-welcome-heading">
          <div className="cp-portal-home__welcome-row">
            <div>
              <p className="cp-portal-home__eyebrow">OCD Homework Assistant</p>
              <h1 id="portal-welcome-heading">
                {clinicianName ? `Welcome, ${clinicianName}` : "Welcome"}
              </h1>
              {clinicNames.length ? (
                <p className="cp-portal-home__clinic">
                  <Building2 aria-hidden="true" />
                  <strong>{clinicNames.join(" • ")}</strong>
                </p>
              ) : null}
              <p className="cp-portal-home__intro">
                Manage your clients, activity templates, and homework
                assignments.
              </p>
            </div>
            <div className="cp-portal-home__assistant-action">
              <ClinicianAssistantHeaderButton />
            </div>
          </div>
          {state.status === "error" ? (
            <div className="cp-portal-home__feedback">
              <ErrorState error={state.error} onRetry={state.refresh} />
            </div>
          ) : null}
          {data && !available ? (
            <div className="cp-portal-home__feedback">
              <ErrorState
                error={new Error("Clinician portal is not available")}
                onRetry={state.refresh}
              />
            </div>
          ) : null}
          {isSupervisor ? (
            <section
              className="cp-portal-team-overview"
              aria-labelledby="portal-team-overview-heading"
            >
              <h2 id="portal-team-overview-heading">Team Overview</h2>
              <div className="cp-team-overview-card">
                <span
                  className="cp-team-overview-card__icon"
                  aria-hidden="true"
                >
                  <Users />
                </span>
                <div className="cp-team-overview-card__content">
                  <h3>Your clinic team</h3>
                  <p>
                    Review the therapists and supervisors assigned to your
                    clinics.
                  </p>
                  {teamAvailable && teamData ? (
                    <div
                      className="cp-team-overview-card__metrics"
                      aria-label="Team summary"
                    >
                      <span>
                        <strong>{teamData.summary.teamMemberCount}</strong> team
                        members
                      </span>
                      <span>
                        <strong>{teamData.summary.therapistCount}</strong>{" "}
                        therapists
                      </span>
                      <span>
                        <strong>{teamData.summary.supervisorCount}</strong>{" "}
                        supervisors
                      </span>
                    </div>
                  ) : teamUnavailable ? (
                    <p className="cp-team-overview-card__status">
                      Team data is temporarily unavailable.
                    </p>
                  ) : (
                    <p className="cp-team-overview-card__status">
                      Loading team overview…
                    </p>
                  )}
                </div>
                <div className="cp-team-overview-card__actions">
                  {teamUnavailable ? (
                    <button
                      type="button"
                      className="cp-button cp-button--secondary"
                      onClick={teamState.refresh}
                    >
                      Try again
                    </button>
                  ) : null}
                  <PortalLink
                    className="cp-button cp-button--primary"
                    path="/clinician-portal/team"
                    clinicianCode={clinicianCode}
                  >
                    View Team
                  </PortalLink>
                </div>
              </div>
            </section>
          ) : null}
          {advancedDashboardEnabled === true ? (
            <section
              className="cp-portal-attention"
              aria-labelledby="portal-attention-heading"
            >
              <h2 id="portal-attention-heading">
                {isSupervisor ? "Team At a Glance" : "At a Glance"}
              </h2>
              {attentionState.status === "error" ||
              (attentionData && !attentionAvailable) ? (
                <div className="cp-attention-error" role="alert">
                  <AlertTriangle aria-hidden="true" />
                  <span>At a Glance data is temporarily unavailable.</span>
                  <button
                    type="button"
                    className="cp-button cp-button--secondary"
                    onClick={attentionState.refresh}
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <DashboardNeedsAttention
                  clinicianCode={clinicianCode}
                  report={attentionAvailable ? attentionData : undefined}
                  loading={attentionLoading}
                  scope={isSupervisor ? "team" : "personal"}
                />
              )}
            </section>
          ) : null}
          <section
            className="cp-portal-quick-actions"
            aria-labelledby="portal-quick-actions-heading"
          >
            <h2 id="portal-quick-actions-heading">Quick Actions</h2>
            <div className="cp-portal-cards">
              <PortalLink
                className="cp-portal-card cp-portal-card--quick-action cp-portal-card--quick-action-client"
                path={addClientPath}
                clinicianCode={clinicianCode}
              >
                <span className="cp-portal-card__icon" aria-hidden="true">
                  <UserPlus />
                </span>
                <h3>Add Client</h3>
                <p>Create a new client record.</p>
              </PortalLink>
              <PortalLink
                className="cp-portal-card cp-portal-card--quick-action cp-portal-card--quick-action-template"
                path={addActivityTemplatePath}
                clinicianCode={clinicianCode}
              >
                <span className="cp-portal-card__icon" aria-hidden="true">
                  <FilePlus2 />
                </span>
                <h3>Add Activity Template</h3>
                <p>Create a reusable homework template.</p>
              </PortalLink>
              <PortalLink
                className="cp-portal-card cp-portal-card--quick-action cp-portal-card--quick-action-homework"
                path={addHomeworkActivityPath}
                clinicianCode={clinicianCode}
              >
                <span className="cp-portal-card__icon" aria-hidden="true">
                  <ClipboardPlus />
                </span>
                <h3>Add Homework Activity</h3>
                <p>Assign a homework activity to a client.</p>
              </PortalLink>
            </div>
          </section>
          <section
            className="cp-portal-manage"
            aria-labelledby="portal-manage-heading"
          >
            <h2 id="portal-manage-heading">Manage</h2>
            <div className="cp-portal-cards">
              <PortalLink
                className="cp-portal-card"
                path="/clinician-portal/clients"
                clinicianCode={clinicianCode}
              >
                <span className="cp-portal-card__top">
                  <span className="cp-portal-card__icon" aria-hidden="true">
                    <Users />
                  </span>
                  {clientCount === undefined ? (
                    <span
                      className="cp-portal-card__count-placeholder"
                      data-testid="client-count-placeholder"
                      aria-hidden="true"
                    />
                  ) : (
                    <strong className="cp-portal-card__count">
                      {clientCount} {clientCount === 1 ? "client" : "clients"}
                    </strong>
                  )}
                </span>
                <h3>Clients</h3>
                <p>
                  View and manage your clients, profiles, and assigned homework.
                </p>
              </PortalLink>
              <PortalLink
                className="cp-portal-card"
                path="/clinician-portal/activity-templates"
                clinicianCode={clinicianCode}
              >
                <span className="cp-portal-card__top">
                  <span className="cp-portal-card__icon" aria-hidden="true">
                    <BookOpen />
                  </span>
                </span>
                <h3>Activity Templates</h3>
                <p>Browse reusable activities for assigning homework.</p>
              </PortalLink>
              <PortalLink
                className="cp-portal-card"
                path="/clinician-portal/homework-activities"
                clinicianCode={clinicianCode}
              >
                <span className="cp-portal-card__top">
                  <span className="cp-portal-card__icon" aria-hidden="true">
                    <ClipboardCheck />
                  </span>
                </span>
                <h3>Homework Activities</h3>
                <p>View and manage homework assigned across your clients.</p>
              </PortalLink>
            </div>
          </section>
        </section>
      </div>
    </ClinicianPortalLayout>
  );
}
